import { useEffect, useState } from "react";

import { useDispatch } from "react-redux";

import { Card } from "@/components/ui/card";
import { showError, showSuccess } from "@/utils/notification";
import { generateNumericalId } from "@/utils/string";

import type { ReportGeneratorTemplateWithTypeSchema } from "@api/generated/types.gen";

import { useAppForm } from "@components/Form";
import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Input } from "@components/ui/input";

import { useCreateTemplate } from "@hooks/mutations/use-templates";

import { STATE_OPTIONS } from "../constants/states";
import { useTemplateSections } from "../hooks/useTemplateSectionManager";
import { createTemplateFormSchema } from "../schemas/templateFormSchema";
import { AvailableModules } from "./AvailableModules";
import { CreateTemplateStepHeader } from "./CreateTemplateStepHeader";
import { SelectedModulesPanel } from "./SelectedModulesPanel";

type CreateTemplateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  templates: ReportGeneratorTemplateWithTypeSchema[];
};

export const CreateTemplateModal = ({
  isOpen,
  onClose,
  templates,
}: CreateTemplateModalProps) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState<1 | 2>(1);
  const [generatedTemplateId, setGeneratedTemplateId] = useState("");

  const sectionManager = useTemplateSections();

  // Generate UUID when modal opens and reset when closes
  useEffect(() => {
    if (isOpen && !generatedTemplateId) {
      setGeneratedTemplateId(crypto.randomUUID());
    }
    if (!isOpen) {
      setGeneratedTemplateId("");
      setStep(1);
      sectionManager.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, generatedTemplateId]);

  const createTemplate = useCreateTemplate({
    onSuccess: () => {
      showSuccess("Template created successfully!", dispatch);
      onClose();
      setStep(1);
      sectionManager.reset();
      setGeneratedTemplateId("");
    },
    onError: (error) => {
      console.error("Failed to create template:", error);
      showError("Failed to create template. Please try again.", dispatch);
    },
  });

  const form = useAppForm({
    defaultValues: {
      name: "",
      baseTemplateId: "",
      state: "",
      description: "",
    },
    validators: {
      onSubmit: ({ value }) => {
        const result = createTemplateFormSchema.safeParse(value);
        const errors: Record<string, string> = {};

        if (!result.success) {
          result.error.issues.forEach((issue) => {
            if (issue.path[0]) {
              errors[issue.path[0] as string] = issue.message;
            }
          });
        }

        // Check section requirement for step 2
        if (step === 2 && sectionManager.selectedSections.length === 0) {
          return {
            form: "Please select at least one section for the template",
            fields: errors,
          };
        }

        // Return field errors if validation failed
        if (!result.success) {
          return { form: undefined, fields: errors };
        }

        return undefined;
      },
    },
    onSubmit: async ({ value }) => {
      if (step === 1) {
        const baseTemplate = templates.find((t) => t.id === value.baseTemplateId);
        if (baseTemplate?.sections) {
          sectionManager.setAvailableSections(
            baseTemplate.sections.map((section, index) => ({
              id: section.id,
              name: section.name,
              order: index,
            }))
          );
        }
        setStep(2);
      } else {
        if (sectionManager.selectedSections.length === 0) return;

        await createTemplate.mutateAsync({
          template_id: generatedTemplateId,
          base_template_id: value.baseTemplateId,
          name: value.name,
          state: value.state,
          description: value.description,
          selected_sections: sectionManager.selectedSections.map(
            (section, index) => ({
              section_id: section.id,
              order: index,
            })
          ),
        });
      }
    },
  });

  const templateOptions = templates.map((template) => ({
    value: template.id,
    label: template.name,
  }));

  const numericalId = generatedTemplateId
    ? generateNumericalId(generatedTemplateId)
    : "";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="min-w-[960px] max-w-5xl space-y-2 rounded-2xl bg-background px-8 py-10">
        <DialogHeader>
          <DialogTitle>Create New Template</DialogTitle>
        </DialogHeader>

        <form.AppForm>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit(e);
            }}
          >
            {step === 1 && (
              <Card className="p-4">
                <div className="space-y-6 pt-4">
                  <CreateTemplateStepHeader
                    step={1}
                    totalSteps={2}
                    title="Step 1: Template Details"
                  />

                  <div className="grid grid-cols-2 gap-4">
                    {numericalId && (
                      <div className="space-y-1.5">
                        <label className="text-sm font-semibold">Template ID</label>
                        <Input value={numericalId} readOnly disabled />
                      </div>
                    )}
                    <form.AppField name="name">
                      {(field) => (
                        <field.InputField
                          label="Template Name"
                          placeholder="Enter template name"
                          required
                        />
                      )}
                    </form.AppField>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <form.AppField name="baseTemplateId">
                      {(field) => (
                        <field.SelectField
                          label="Base Template"
                          placeholder="Select base template"
                          options={templateOptions}
                          required
                        />
                      )}
                    </form.AppField>

                    <form.AppField name="state">
                      {(field) => (
                        <field.SelectField
                          label="State"
                          placeholder="Select state"
                          options={STATE_OPTIONS}
                          required
                        />
                      )}
                    </form.AppField>
                  </div>

                  <form.AppField name="description">
                    {(field) => (
                      <field.TextAreaField
                        label="Description"
                        placeholder="Enter description (optional)"
                        rows={3}
                      />
                    )}
                  </form.AppField>
                </div>
              </Card>
            )}

            {step === 2 && (
              <Card className="p-4">
                <div className="space-y-6 pt-4">
                  <CreateTemplateStepHeader
                    step={2}
                    totalSteps={2}
                    title="Step 2: Select & Order Modules"
                    description="Select the modules you want to include in your custom template, then drag them to reorder as needed."
                  />

                  <div className="grid grid-cols-2 gap-6">
                    <AvailableModules
                      unselectedSections={sectionManager.unselectedSections}
                      searchQuery={sectionManager.searchQuery}
                      setSearchQuery={sectionManager.setSearchQuery}
                      selectAllChecked={sectionManager.selectAllChecked}
                      checkedSections={sectionManager.checkedSections}
                      onSelectAll={sectionManager.handleSelectAll}
                      onCheckSection={sectionManager.handleCheckSection}
                      onAddSelected={sectionManager.handleAddSelected}
                    />

                    <SelectedModulesPanel
                      selectedSections={sectionManager.selectedSections}
                      totalAvailable={sectionManager.availableSections.length}
                      draggedIndex={sectionManager.draggedIndex}
                      dragOverIndex={sectionManager.dragOverIndex}
                      onRemove={sectionManager.handleRemoveSection}
                      onDragStart={sectionManager.handleDragStart}
                      onDragEnd={sectionManager.handleDragEnd}
                      onDragOver={sectionManager.handleDragOver}
                      onDrop={sectionManager.handleDrop}
                    />
                  </div>
                </div>
              </Card>
            )}

            {/* Footer */}
            <div className="flex justify-between pt-6">
              {step === 1 ? (
                <>
                  <div />
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button type="submit">Next Step</Button>
                  </div>
                </>
              ) : (
                <>
                  <Button type="button" variant="outline" onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        sectionManager.selectedSections.length === 0 ||
                        createTemplate.isPending
                      }
                    >
                      {createTemplate.isPending ? "Creating..." : "Create Template"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
};
